import { Box, debounce, Skeleton, Stack, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ChangeEvent,
  type MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SearchBar } from 'src/components/SearchBar/search-bar';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { Address, Client, Conversation } from 'src/graphql';
import { DEFAULT_PAGINATION_LIMIT, useSearch } from 'src/graphql/entities/search/use-search';

import { ConditionInput, EntityNames } from 'src/graphql/types';
import { ConversationInfo, ConversationTooltipContent } from './conversation-info';
import { ConversationTooltip } from './conversation-tooltip';

interface RecentConversationsProps {
  conversation?: Conversation;
  address?: Partial<Address>;
  client?: Partial<Client>;
}

const SKELETON_ITEMS = 5;
const TOOLTIP_ENTER_DELAY = 500;
const TOOLTIP_LEAVE_DELAY = 500;
const TOOLTIP_SWITCH_DELAY = 500;

export const ConversationInfoSkeleton = () => (
  <Stack direction="row" spacing={3} alignItems="center">
    <Skeleton variant="circular" width={20} height={20} />
    <Stack
      direction="row"
      width="calc(100% - 15px)"
      justifyContent="space-between"
      spacing={2}
    >
      <Skeleton width="70%" />
      <Skeleton width="30%" />
    </Stack>
  </Stack>
);

export const RecentConversations = ({
  conversation,
  address,
  client,
}: RecentConversationsProps) => {
  const baseFilters: ConditionInput[] = useMemo(() => {
    const filters: ConditionInput[] = [];

    if (address?.addressId) {
      filters.push({
        attribute: 'associatedAddresses/addressId',
        operator: '=',
        value: address.addressId,
      });
    }

    if (client?.clientId) {
      filters.push({
        attribute: 'requester/participantIdentity',
        operator: '=',
        value: client.clientId,
        logicalOperator: 'or',
      });
      filters.push({
        attribute: 'associatedAddresses/clients/clientId',
        operator: '=',
        value: client.clientId,
        logicalOperator: 'or',
      });
    }

    if (conversation?.conversationId) {
      filters.push({
        attribute: 'conversationId',
        operator: '!=',
        value: conversation.conversationId,
      });
    }

    return filters;
  }, [address?.addressId, client?.clientId, conversation?.conversationId]);

  const [searchTerm, setSearchTerm] = useState('');
  const [tooltipAnchor, setTooltipAnchor] = useState<HTMLElement | null>(null);
  const [tooltipConversation, setTooltipConversation] = useState<Conversation | null>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const tooltipEnterTimerRef = useRef<number | null>(null);
  const tooltipLeaveTimerRef = useRef<number | null>(null);
  const pendingTooltipRef = useRef<{ anchor: HTMLElement; conversation: Conversation } | null>(
    null,
  );

  const {
    records: conversations,
    loadMore,
    isLoading,
    hasMore,
    isLoadingMore,
  } = useSearch({
    entityName: EntityNames.Conversation,
    searchTerm,
    filter: baseFilters,
    limit: DEFAULT_PAGINATION_LIMIT * 3,
    skip: !address && !client,
    showResultsWhenEmptySearchTerm: true,
    sort: [
      { attribute: 'status/sortOrder', desc: false },
      { attribute: 'createdAt', desc: true },
    ],
  }) ?? {};

  const handleOnChange = useMemo(
    () =>
      debounce(({ target }: ChangeEvent) => {
        const value = (target as HTMLInputElement).value;
        setSearchTerm(value);
      }, SEARCH_DEBOUNCE_DELAY),
    [],
  );

  const parentRef = useRef<HTMLDivElement>(null);
  let virtualizerCount = 0;

  if (conversations?.length) {
    virtualizerCount = hasMore ? conversations.length + 1 : conversations.length;
  }

  const rowVirtualizer = useVirtualizer({
    count: virtualizerCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    if (!conversations) {
      return;
    }

    const lastItem = virtualItems[virtualItems.length - 1];

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= conversations.length - 1 && hasMore && !isLoading) {
      void loadMore?.();
    }
  }, [conversations, hasMore, isLoading, loadMore, virtualItems]);

  const clearTooltipTimer = (timerRef: MutableRefObject<number | null>) => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleTooltipOpen = useCallback(
    (conversationItem: Conversation | undefined, anchorEl: HTMLElement) => {
      if (!conversationItem) {
        return;
      }

      clearTooltipTimer(tooltipLeaveTimerRef);
      clearTooltipTimer(tooltipEnterTimerRef);

      if (isTooltipOpen) {
        // Store pending data - keep current tooltip visible in place until switch delay passes
        pendingTooltipRef.current = { anchor: anchorEl, conversation: conversationItem };
        setIsTooltipOpen(false);

        tooltipEnterTimerRef.current = window.setTimeout(() => {
          if (pendingTooltipRef.current) {
            setTooltipAnchor(pendingTooltipRef.current.anchor);
            setTooltipConversation(pendingTooltipRef.current.conversation);
            pendingTooltipRef.current = null;
            setIsTooltipOpen(true);
          }
        }, TOOLTIP_SWITCH_DELAY);
      } else {
        pendingTooltipRef.current = null;
        setTooltipAnchor(anchorEl);
        setTooltipConversation(conversationItem);

        tooltipEnterTimerRef.current = window.setTimeout(() => {
          setIsTooltipOpen(true);
        }, TOOLTIP_ENTER_DELAY);
      }
    },
    [isTooltipOpen],
  );

  const handleTooltipClose = useCallback(() => {
    clearTooltipTimer(tooltipEnterTimerRef);
    clearTooltipTimer(tooltipLeaveTimerRef);
    pendingTooltipRef.current = null;
    tooltipLeaveTimerRef.current = window.setTimeout(() => {
      setIsTooltipOpen(false);
      setTooltipAnchor(null);
      setTooltipConversation(null);
    }, TOOLTIP_LEAVE_DELAY);
  }, []);

  useEffect(() => {
    return () => {
      clearTooltipTimer(tooltipEnterTimerRef);
      clearTooltipTimer(tooltipLeaveTimerRef);
    };
  }, []);

  const tooltipTitle = tooltipConversation ? (
    <ConversationTooltipContent conversation={tooltipConversation} />
  ) : (
    ''
  );

  const isTooltipVisible = isTooltipOpen && !!tooltipConversation && !!tooltipAnchor;

  return (
    <Stack spacing={1} flexGrow={1} minHeight={0}>
      <SearchBar onChange={handleOnChange} title="Associated Conversations" />
      <ConversationTooltip
        anchorEl={tooltipAnchor}
        enterDelay={TOOLTIP_ENTER_DELAY}
        isOpen={isTooltipVisible}
        leaveDelay={TOOLTIP_LEAVE_DELAY}
        title={tooltipTitle}
      >
        <Box
          flexGrow={1}
          ref={parentRef}
          sx={{
            marginTop: (theme) => theme.utils.important(0),
            gridAutoRows: 'min-content',
          }}
        >
          {isLoading && !isLoadingMore
            ? Array.from({ length: SKELETON_ITEMS }, (_, i) => (
                <ConversationInfoSkeleton key={i} />
              ))
            : !!conversations && (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const isLoaderRow = virtualRow.index > conversations.length - 1;
                    const conversation = conversations[virtualRow.index];

                    return (
                      <div
                        key={virtualRow.key}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `100%`,
                          height: `${virtualRow.size}px`,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {isLoaderRow && hasMore ? (
                          <Typography variant="body2">Loading more...</Typography>
                        ) : (
                          <ConversationInfo
                            key={`recent-${conversation?.conversationId}`}
                            conversation={conversation}
                            onTooltipOpen={handleTooltipOpen}
                            onTooltipClose={handleTooltipClose}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

          {!isLoading && !conversations?.length && (
            <Stack padding="0.5rem">
              <Typography alignSelf="center" variant="body2">
                No results
              </Typography>
            </Stack>
          )}
        </Box>
      </ConversationTooltip>
    </Stack>
  );
};
